// SaveAttributeFlow — one workspace-attribute write, end to end: pick the verb, shape the body,
// send it, re-hydrate 07 (contract 22 §2, CM-22-01). Exported on the barrel so a caller that
// batches several keys (30 does, ~20 of them) drives this directly instead of the composable.
//
// Headless by contract: it returns an outcome and never toasts or resolves i18n — the pane owns
// the copy (§13). A failed WRITE propagates its `ApiError` to the caller; a failed REFRESH does
// not, because the write still landed (§14).
import { WorkspaceAttributeApi } from '../api/WorkspaceAttributeApi'
import type {
  WorkspaceAttributeId,
  WorkspaceAttributeKey,
  WorkspaceAttributeRegistry,
} from '../models/attribute.types'
import { attributeBody, attributeVerb, type AttributeVerb } from '../service/attributeEnvelope'

import { useAccount, useWorkspace } from '@/features/account'

export interface SaveAttributeOptions {
  /** The existing row's id, or `null`/`undefined` when the row does not exist yet. This is what
   *  selects create-vs-update; read it off `useWorkspaceAttribute().id` or 07's `getAttribute`. */
  attributeId: WorkspaceAttributeId
  /** ⚠ R8 — write to a workspace OTHER than the selected one. Onboarding needs it
   *  (`OnboardingStep1.vue:89` writes `main_domain` to a workspace that is not selected yet);
   *  without it module 29 would fork the verb rule. Defaults to the selected workspace. */
  workspaceId?: number | string
}

export interface SaveAttributeResult {
  verb: AttributeVerb
  /** `false` only for a `noop` — the write was resolved as "nothing to send", not attempted. */
  written: boolean
  /** The write landed but the re-read did not: show the optimistic value plus a non-blocking
   *  `settings.common.refreshFailed` with Retry (§14). A write to another workspace refreshes
   *  nothing and is not a failure. */
  refreshFailed: boolean
}

export interface AttributeBatchItem {
  key: WorkspaceAttributeKey
  value: WorkspaceAttributeRegistry[WorkspaceAttributeKey] | null
  attributeId: WorkspaceAttributeId
}

export interface SaveAttributeBatchResult {
  results: SaveAttributeResult[]
  refreshFailed: boolean
}

export interface SaveAttributeFlowOptions {
  api?: WorkspaceAttributeApi
}

export class SaveAttributeFlow {
  private readonly api: WorkspaceAttributeApi

  constructor(options: SaveAttributeFlowOptions = {}) {
    this.api = options.api ?? new WorkspaceAttributeApi()
  }

  async save<K extends WorkspaceAttributeKey>(
    key: K,
    value: WorkspaceAttributeRegistry[K] | null,
    options: SaveAttributeOptions,
  ): Promise<SaveAttributeResult> {
    const verb = attributeVerb(options.attributeId, value)
    // Clearing an attribute that was never set sends nothing at all (§6.1, §14).
    if (verb === 'noop') return { verb, written: false, refreshFailed: false }

    const { accountId } = useAccount()
    const { workspaceId, refreshAttributes } = useWorkspace()

    const selectedWorkspaceId = workspaceId.value
    const targetWorkspaceId = options.workspaceId ?? selectedWorkspaceId
    if (accountId.value === undefined || targetWorkspaceId === undefined) {
      throw new Error('SaveAttributeFlow: no account/workspace in scope for an attribute write')
    }

    await this.api.write({
      verb,
      accountId: accountId.value,
      workspaceId: targetWorkspaceId,
      body: attributeBody(verb, options.attributeId, key, value),
    })

    // 07's store only ever holds the SELECTED workspace's blob, so a write aimed elsewhere (R8)
    // has nothing to re-hydrate. Ids cross the boundary as both numbers and strings — compare
    // as strings so a `'12'` from a route param still matches a numeric `12`.
    if (String(targetWorkspaceId) !== String(selectedWorkspaceId)) {
      return { verb, written: true, refreshFailed: false }
    }

    return { verb, written: true, refreshFailed: !(await refreshAttributes()) }
  }

  /** Write a related attribute set in order, then re-hydrate the selected workspace once. */
  async saveBatch(
    items: readonly AttributeBatchItem[],
    options: { workspaceId?: number | string } = {},
  ): Promise<SaveAttributeBatchResult> {
    const { accountId } = useAccount()
    const { workspaceId, refreshAttributes } = useWorkspace()

    const selectedWorkspaceId = workspaceId.value
    const targetWorkspaceId = options.workspaceId ?? selectedWorkspaceId
    if (accountId.value === undefined || targetWorkspaceId === undefined) {
      throw new Error('SaveAttributeFlow: no account/workspace in scope for an attribute write')
    }

    const results: SaveAttributeResult[] = []
    for (const item of items) {
      const verb = attributeVerb(item.attributeId, item.value)
      if (verb === 'noop') {
        results.push({ verb, written: false, refreshFailed: false })
        continue
      }

      await this.api.write({
        verb,
        accountId: accountId.value,
        workspaceId: targetWorkspaceId,
        body: attributeBody(verb, item.attributeId, item.key, item.value),
      })
      results.push({ verb, written: true, refreshFailed: false })
    }

    const wrote = results.some((result) => result.written)
    const refreshFailed =
      wrote && String(targetWorkspaceId) === String(selectedWorkspaceId)
        ? !(await refreshAttributes())
        : false

    return { results, refreshFailed }
  }
}
